import React, { useMemo, useState, useCallback } from "react";
import Stack from "@mui/material/Stack";
import useJumboLayoutSidebar from "@jumbo/hooks/useJumboLayoutSidebar";
import AuthUserDropdown from "../../../../shared/widgets/AuthUserDropdown";
import {
  Button,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
} from "@mui/material";
import Div from "@jumbo/shared/Div";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import Logo from "../../../../shared/Logo";
import { SIDEBAR_STYLES, SIDEBAR_VARIANTS } from "@jumbo/utils/constants";
import useJumboHeaderTheme from "@jumbo/hooks/useJumboHeaderTheme";
import { useLocation, useNavigate } from "react-router-dom";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import { isSideBarAllowed } from "app/utils/permissions";
import { ROUTES } from "app/utils/constants/routeConstants";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import LoginIcon from "@mui/icons-material/Login";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import CodeIcon from "@mui/icons-material/Code";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";

const Header = () => {
  const { sidebarOptions, setSidebarOptions } = useJumboLayoutSidebar();
  const { headerTheme } = useJumboHeaderTheme();
  const location = useLocation();
  const { authUser } = useJumboAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const userRoles = useMemo(
    () => (authUser && authUser.authorities ? authUser.authorities : []),
    [authUser]
  );

  const showMenuIcon = useMemo(
    () => isSideBarAllowed(location.pathname, userRoles),
    [location.pathname, userRoles]
  );

  // navigate to sign-in with redirectURI
  const navigateToSignIn = useCallback(() => {
    const redirectURI = encodeURIComponent(location.pathname);
    navigate(`${ROUTES.USER_LOGIN}?redirectURI=${redirectURI}`);
    setMenuOpen(false);
    setAnchorEl(null);
  }, [location.pathname, navigate]);

  const handleMenuToggle = useCallback((event) => {
    setMenuOpen((prev) => {
      const newState = !prev;
      setAnchorEl(newState ? event.currentTarget : null);
      return newState;
    });
  }, []);

  const handleMenuClose = useCallback(() => {
    setMenuOpen(false);
    setAnchorEl(null);
  }, []);

  return (
    <React.Fragment>
      <Stack
        direction="row"
        alignItems="center"
        sx={{ flex: 1, minHeight: 64 }}
      >
        {/* Sidebar Toggle Icon */}
        {(sidebarOptions.style === SIDEBAR_STYLES.CLIPPED_UNDER_HEADER ||
          sidebarOptions.variant === SIDEBAR_VARIANTS.TEMPORARY ||
          (sidebarOptions.variant === SIDEBAR_VARIANTS.PERSISTENT &&
            !sidebarOptions.open)) &&
          showMenuIcon && (
            <IconButton
              edge="start"
              color="inherit"
              aria-label={sidebarOptions.open ? "close drawer" : "open drawer"}
              aria-expanded={sidebarOptions.open}
              sx={{
                ml:
                  sidebarOptions.style === SIDEBAR_STYLES.CLIPPED_UNDER_HEADER
                    ? -2
                    : 0,
                mr: 3,
              }}
              onClick={() => setSidebarOptions({ open: !sidebarOptions.open })}
            >
              {sidebarOptions.open ? <MenuOpenIcon /> : <MenuIcon />}
            </IconButton>
          )}

        <Div sx={{ mt: 0, display: "flex", flexDirection: "row" }}>
          <Logo sx={{ mr: 2 }} mode={headerTheme.type ?? "light"} />
        </Div>

        <Stack
          direction="row"
          alignItems="center"
          sx={{
            ml: "auto",
            display: { xs: "none", lg: "flex" },
          }}
        >
          {authUser ? (
            <AuthUserDropdown />
          ) : (
            <>
              <Button
                onClick={() => navigate(ROUTES.ALGOS_AND_DS)}
                size="small"
                sx={{
                  color: "#e0e0e0",
                  textTransform: "none",
                  fontSize: "1rem",
                  px: 2,
                  "&:hover": {
                    color: "#222d45",
                    backgroundColor: "#e0e0e0",
                  },
                }}
                aria-label="View DSA problems"
              >
                DSA problems
              </Button>
              <Button
                onClick={navigateToSignIn}
                size="small"
                sx={{
                  color: "#e0e0e0",
                  textTransform: "none",
                  fontSize: "1rem",
                  px: 2,
                  "&:hover": {
                    color: "#222d45",
                    backgroundColor: "#e0e0e0",
                  },
                }}
                aria-label="Sign in"
              >
                Sign In
              </Button>
              <Button
                onClick={() => navigate(ROUTES.USER_SIGNUP)}
                size="small"
                sx={{
                  color: "#e0e0e0",
                  textTransform: "none",
                  fontSize: "1rem",
                  px: 2,
                  "&:hover": {
                    color: "#222d45",
                    backgroundColor: "#e0e0e0",
                  },
                }}
                aria-label="Sign up"
              >
                Sign Up
              </Button>
              <Button
                onClick={() => navigate(ROUTES.UPGRADE)}
                size="small"
                sx={{
                  color: "#e0e0e0",
                  textTransform: "none",
                  fontSize: "1rem",
                  px: 2,
                  "&:hover": {
                    color: "#222d45",
                    backgroundColor: "#e0e0e0",
                  },
                }}
                aria-label="Upgrade plan"
              >
                Upgrade
              </Button>
            </>
          )}
        </Stack>

        {/* Dropdown Menu or AuthUserDropdown for Smaller Screens */}
        <Div
          sx={{
            display: { xs: "flex", lg: "none" },
            alignItems: "center",
            ml: "auto",
          }}
        >
          {authUser ? (
            <AuthUserDropdown />
          ) : (
            <>
              <IconButton
                color="inherit"
                aria-label={menuOpen ? "close menu" : "open menu"}
                aria-expanded={menuOpen}
                sx={{
                  p: 1,
                  display: { xs: "flex", lg: "none" }, // Explicitly show on smaller screens
                  visibility: "visible",
                  opacity: 1,
                }}
                onClick={handleMenuToggle}
              >
                {menuOpen ? <CloseIcon /> : <MenuIcon />}
              </IconButton>
              <Popover
                anchorEl={anchorEl}
                open={menuOpen}
                onClose={handleMenuClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                sx={{
                  top: "8px",
                }}
              >
                <List
                  disablePadding
                  sx={{
                    minWidth: 200,
                    bgcolor: "background.paper",
                    borderRadius: 1,
                  }}
                >
                  <ListItemButton
                    onClick={() => {
                      navigate(ROUTES.ALGOS_AND_DS);
                      setMenuOpen(false);
                      setAnchorEl(null);
                    }}
                    sx={{ py: 1.5 }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CodeIcon />
                    </ListItemIcon>
                    <ListItemText primary="DSA problems" />
                  </ListItemButton>
                  <ListItemButton onClick={navigateToSignIn} sx={{ py: 1.5 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <LoginIcon />
                    </ListItemIcon>
                    <ListItemText primary="Sign In" />
                  </ListItemButton>
                  <ListItemButton
                    onClick={() => {
                      navigate(ROUTES.USER_SIGNUP);
                      setMenuOpen(false);
                      setAnchorEl(null);
                    }}
                    sx={{ py: 1.5 }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <PersonAddIcon />
                    </ListItemIcon>
                    <ListItemText primary="Sign Up" />
                  </ListItemButton>
                  <ListItemButton
                    onClick={() => {
                      navigate(ROUTES.UPGRADE);
                      setMenuOpen(false);
                      setAnchorEl(null);
                    }}
                    sx={{ py: 1.5 }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CreditCardIcon />
                    </ListItemIcon>
                    <ListItemText primary="Upgrade" />
                  </ListItemButton>
                </List>
              </Popover>
            </>
          )}
        </Div>
      </Stack>
    </React.Fragment>
  );
};

export default Header;
